import { Component, OnInit } from '@angular/core';
import { faCalendar, faClipboardCheck, faDollarSign, faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons';
import { AnalyticsService } from '../analytics.service';
import { Chart, registerables, CategoryScale } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ProjectService } from '../project.service';
import dayjs from 'dayjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { ReactiveFormsModule } from '@angular/forms';
import { NgIf, NgFor } from '@angular/common';
import { Subscription } from 'rxjs';
import { DarkModeService } from '@app-shared/services/dark-mode.service';

// Register required components
Chart.register(...registerables, CategoryScale);

@Component({
  selector: 'app-dashboard-stats',
  templateUrl: './dashboard-stats.component.html',
  styleUrls: ['./dashboard-stats.component.css'],
  imports: [NgIf, NgFor, ReactiveFormsModule, NgbInputDatepicker, FaIconComponent]
})
export class DashboardStatsComponent implements OnInit {

  // Properties
  years: string[] = [];
  selectedYear: string;
  cashFlowInsights: any;
  chart: Chart;

  // Font Awesome Properties
  faClipboardCheck = faClipboardCheck;
  faDollarSign = faDollarSign;
  faHandHoldingUsd = faHandHoldingUsd;
  faCalendar = faCalendar;
  gridColor: string = '#dddddd';
  legendColor: string = '#000000';
  dataLabelColor: string = '#000000';
  isDarkMode: boolean;
  private darkModeSubscription: Subscription;
  constructor(private analyticsService: AnalyticsService,
    private projectService: ProjectService,
    private darkModeService: DarkModeService) { }

  ngOnInit(): void {
    this.darkModeSubscription = this.darkModeService.darkModeChanged$.subscribe((isDarkModeEnabled) => {
      if (isDarkModeEnabled) {
        this.gridColor = '#444444';
        this.legendColor = '#ffffff';
        this.dataLabelColor = '#ffffff';
      }
      this.updateChart(isDarkModeEnabled);
    });
    this.getDistinctProjectYears();
  }

  ngOnDestroy(): void {
    if (this.darkModeSubscription) {
      this.darkModeSubscription.unsubscribe();
    }
  }

  updateChart(isDarkMode: boolean): void {
    if (this.chart) {
      this.chart.options.plugins.legend.labels.color = isDarkMode ? '#ffffff' : '#000000';
      this.chart.options.plugins.datalabels.color = isDarkMode ? '#ffffff' : '#000000';
      this.chart.options.scales.x.grid.color = isDarkMode ? '#444444' : '#eeeeee';
      this.chart.options.scales.y.grid.color = isDarkMode ? '#444444' : '#eeeeee';
      this.chart.update();
    }
  }

  // Year Selected
  yearSelected(year: string): void {
    this.selectedYear = year;
    this.getCashFlowInsights();
  }

  // Get Distinct Project Years
  private getDistinctProjectYears(): void {
    const params = {
      searchTerm: null,
      sortBy: 'p.project_year',
      sortDirection: 'DESC',
      limit: null,
      offset: null,
      column: 'p.project_year'
    };
    this.projectService.getDistinctColumnValues(params).subscribe((values) => {
      this.years = values.map((value) => { return value.toString(); });
      if (this.years.length > 0) {
        const currentYear = dayjs().year().toString();
        this.selectedYear = this.years.includes(currentYear) ? currentYear : this.years[0];
        this.getCashFlowInsights();
      }
    });
  }

  // Get Cash Flow Insights
  private getCashFlowInsights(): void {
    const params = {
      year: this.selectedYear
    };
    this.analyticsService.getCashFlowInsights(params).subscribe((res) => {
      this.cashFlowInsights = res;
      if (!this.chart) {
        this.generateChart();
      } else {
        this.chart.data.datasets[0].data = [this.cashFlowInsights.acceptedProposalsTotal];
        this.chart.data.datasets[1].data = [0, this.cashFlowInsights.pendingPaymentsTotal];
        this.chart.data.datasets[2].data = [0, this.cashFlowInsights.closedPaymentsTotal];
        this.chart.data.datasets[3].data = [0, 0, this.cashFlowInsights.unpaidTotal];
        this.chart.update();
      }
    });
  }

  // Generate Chart
  private generateChart(): void {
    const ctx = (document.getElementById('CHART') as HTMLCanvasElement);

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: [['Accepted', 'Proposals'], 'Paid', 'Unpaid'],
        datasets: [
          {
            label: 'Accepted Proposals',
            data: [this.cashFlowInsights.acceptedProposalsTotal],
            backgroundColor: ['rgba(54, 162, 235, 0.5)']
          },
          {
            label: 'Payments (Pending)',
            data: [0, this.cashFlowInsights.pendingPaymentsTotal],
            backgroundColor: ['rgba(255, 159, 64, 0.5)']
          },
          {
            label: 'Payments (Closed & Settled)',
            data: [0, this.cashFlowInsights.closedPaymentsTotal],
            backgroundColor: ['rgba(75, 192, 192, 0.5)']
          },
          {
            label: 'Unpaid',
            data: [0, 0, this.cashFlowInsights.unpaidTotal],
            backgroundColor: ['rgba(255, 99, 132, 0.5)']
          }
        ]
      },
      plugins: [ChartDataLabels],
      options: {
        // remove `events: null` so hover/tooltip works
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 15
          }
        },
        scales: {
          x: {
            display: false,
            stacked: true,
            grid: {
              color: this.gridColor
            }
          },
          y: {
            stacked: true,
            ticks: {
              callback: (value) => {
                return '$' + value;
              }
            },
            grid: {
              color: this.gridColor
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: this.legendColor,
              font: {
                family: "'Poppins', sans-serif"
              }
            },
            position: 'bottom'
          },
          tooltip: {
            enabled: true,
            mode: 'nearest',   // <- show only the bar you're hovering
            intersect: true,   // <- require actual intersection with that bar
            callbacks: {
              // Show dataset label as title (e.g. "Payments (Pending)")
              title: (items) => {
                const item = items[0];
                return item.dataset.label || '';
              },
              // Show formatted value
              label: (context) => {
                const value = context.raw as number;
                if (!value) return '';
                const currency = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(value);
                return currency;
              },
              // Show explanation per dataset
              afterBody: (items) => {
                const dsIndex = items[0].datasetIndex;
                const tooltipDescriptions = [
                  'Total amount of accepted proposals.',
                  'Includes payments with status: Pending & Approved.',
                  'Includes payments that are already received by the merchants.',
                  'Includes invoices without payments, and payments with status: Rejected, Notice of Change, Voided & Returned.'
                ];
                return tooltipDescriptions[dsIndex] ?? '';
              }
            }
          },
          datalabels: {
            align: 'top',
            anchor: 'end',
            color: this.dataLabelColor,
            font: {
              family: "'Poppins', sans-serif",
              weight: 600
            },
            formatter: (value) => {
              return (value > 0)
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
                : null;
            },
            offset: -2.5,
            textAlign: 'center'
          }
        }

      }
    });

  }
}
